import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import SecretarySidebar from '../../components/SecretarySidebar';
import axios from 'axios';

// Fonction pour obtenir l'année scolaire courante
function getCurrentSchoolYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 9) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
}

// Fonction pour générer les années scolaires disponibles
const getSchoolYears = (count = 5) => {
  const current = getCurrentSchoolYear();
  const startYear = parseInt(current.split('-')[0], 10);
  const now = new Date();
  const includeNextYear = now.getMonth() >= 4;
  const start = includeNextYear ? startYear + 1 : startYear;
  const length = includeNextYear ? count + 1 : count;
  return Array.from({ length: length }, (_, i) => {
    const y = start - i;
    return `${y}-${y + 1}`;
  }).reverse();
};

const Classes = () => {
  const [classes, setClasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [newClass, setNewClass] = useState({
    name: '',
    level: '',
    amount: '',
    amount_old_students: '',
    cantine_amount: '',
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editClass, setEditClass] = useState<any | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [existingAmount, setExistingAmount] = useState<string | null>(null);
  const [schoolYear, setSchoolYear] = useState(getCurrentSchoolYear());

  useEffect(() => {
    let isMounted = true;
    
    const fetchClasses = async () => {
      if (!isMounted) return;
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('https://lapetiteacademie.ci/api/classes', {
          headers: { Authorization: `Bearer ${token}` },
          params: { school_year: schoolYear }
        });
        if (isMounted) {
          const normalized = res.data.map((c: any) => ({ ...c, id: c.id || c._id }));
          setClasses(normalized);
        }
      } catch (err: any) {
        if (isMounted) setError(err.response?.data?.message || 'Erreur lors du chargement des classes');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchClasses();
    
    return () => {
      isMounted = false;
    };
  }, [schoolYear]);

  useEffect(() => {
    if (existingAmount) {
      // Extraire le montant numérique de la chaîne (ex: "120000.00 F CFA (classe: 5 ème1)")
      const match = existingAmount.match(/([\d.,]+)/);
      if (match) {
        setNewClass(prev => ({ ...prev, amount: match[1].replace(',', '') }));
      }
    }
  }, [existingAmount]);

  const filteredClasses = classes.filter((classe) => {
    // On adapte les champs selon la structure de la classe reçue du backend
    const nom = classe.nom || classe.name || '';
    const niveau = classe.niveau || classe.level || '';
    const prof = classe.professeurPrincipal || classe.teacher || '';
    return (
      nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prof.toLowerCase().includes(searchTerm.toLowerCase()) ||
      niveau.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Ajout d'une classe
  const handleAddClass = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://lapetiteacademie.ci/api/classes', {
        name: newClass.name,
        level: newClass.level,
        academic_year: new Date().getFullYear().toString(),
        amount: newClass.amount,
        amount_old_students: newClass.amount_old_students,
        cantine_amount: newClass.cantine_amount,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Recharge la liste
      const res = await axios.get('https://lapetiteacademie.ci/api/classes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(res.data);
      setOpen(false);
      setNewClass({ name: '', level: '', amount: '', amount_old_students: '', cantine_amount: '' });
      setSnackbar({ open: true, message: 'Classe ajoutée avec succès', severity: 'success' });
    } catch (err: any) {
      console.error("Erreur lors de l'ajout de la classe :", err, err?.response);
      setSnackbar({ open: true, message: err.response?.data?.message || "Erreur lors de l'ajout de la classe", severity: 'error' });
    }
  };

  // Suppression
  const handleDelete = async (id: number | string) => {
    if (!id) {
      console.error('Impossible de supprimer : id manquant');
      setSnackbar({ open: true, message: 'ID de classe manquant', severity: 'error' });
      return;
    }
    
    if (window.confirm('Voulez-vous vraiment supprimer cette classe ?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`https://lapetiteacademie.ci/api/classes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Si la suppression réussit, mettre à jour la liste
        setClasses(classes.filter(c => c.id !== id));
        setSnackbar({ open: true, message: response.data.message || 'Classe supprimée avec succès', severity: 'success' });
      } catch (err: any) {
        console.error('Erreur lors de la suppression de la classe :', err, err?.response);
        
        let errorMessage = 'Erreur lors de la suppression';
        
        if (err.response) {
          switch (err.response.status) {
            case 400:
              errorMessage = err.response.data.message || 'Impossible de supprimer cette classe : elle est utilisée';
              break;
            case 404:
              errorMessage = 'Classe non trouvée';
              break;
            case 403:
              errorMessage = 'Vous n\'avez pas les permissions pour supprimer cette classe';
              break;
            default:
              errorMessage = err.response.data.message || 'Erreur serveur lors de la suppression';
          }
        } else if (err.request) {
          errorMessage = 'Erreur de connexion au serveur';
        }
        
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      }
    }
  };

  // Edition
  const handleEditOpen = (classe: any) => {
    if (!classe.id) {
      console.error('Impossible de modifier : id manquant', classe);
      setSnackbar({ open: true, message: 'Erreur : ID de classe manquant', severity: 'error' });
      return;
    }
    setEditClass({
      ...classe,
      name: classe.nom || classe.name || '',
      level: classe.niveau || classe.level || '',
      amount: classe.amount || '',
      amount_old_students: classe.amount_old_students || '',
      cantine_amount: classe.cantine_amount || '',
    });
    setEditOpen(true);
    // Réinitialiser les messages d'erreur
    setExistingAmount(null);
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setEditClass(null);
  };
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { name: string, value: any } }) => {
    setEditClass((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleEditSubmit = async () => {
    if (!editClass) {
      setSnackbar({ open: true, message: 'Aucune classe sélectionnée', severity: 'error' });
      return;
    }

    // Validation des champs obligatoires
    if (!editClass.name || !editClass.level || !editClass.amount) {
      setSnackbar({ open: true, message: 'Veuillez remplir tous les champs obligatoires', severity: 'error' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`https://lapetiteacademie.ci/api/classes/${editClass.id}`, {
        name: editClass.name,
        level: editClass.level,
        academic_year: editClass.academic_year || new Date().getFullYear().toString(),
        amount: editClass.amount,
        amount_old_students: editClass.amount_old_students,
        cantine_amount: editClass.cantine_amount,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Recharge la liste avec les paramètres actuels
      const res = await axios.get('https://lapetiteacademie.ci/api/classes', {
        headers: { Authorization: `Bearer ${token}` },
        params: { school_year: schoolYear }
      });
      
      setClasses(res.data);
      setEditOpen(false);
      setEditClass(null);
      setExistingAmount(null);
      setSnackbar({ open: true, message: 'Classe modifiée avec succès', severity: 'success' });
    } catch (err: any) {
      console.error('Erreur lors de la modification de la classe :', err, err?.response);
      
      let errorMessage = 'Erreur lors de la modification';
      
      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMessage = err.response.data.message || 'Données invalides';
            break;
          case 404:
            errorMessage = 'Classe non trouvée';
            break;
          case 403:
            errorMessage = 'Vous n\'avez pas les permissions pour modifier cette classe';
            break;
          default:
            errorMessage = err.response.data.message || 'Erreur serveur lors de la modification';
        }
      } else if (err.request) {
        errorMessage = 'Erreur de connexion au serveur';
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // Vérifier le montant existant pour un niveau
  const checkExistingAmount = async (level: string, excludeId?: number) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ level });
      if (excludeId) params.append('excludeId', excludeId.toString());
      
      const res = await axios.get(`https://lapetiteacademie.ci/api/classes/check-amount?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.length > 0) {
        setExistingAmount(`${res.data[0].amount} F CFA (classe: ${res.data[0].name})`);
      } else {
        setExistingAmount(null);
      }
    } catch (err) {
      console.error('Erreur lors de la vérification du montant:', err);
    }
  };

  // Ajoute une fonction utilitaire pour extraire le montant numérique de existingAmount
  function extractAmount(existingAmount: string | null) {
    if (!existingAmount) return null;
    const match = existingAmount.match(/([\d.,]+)/);
    if (match) {
      return match[1].replace(',', '');
    }
    return null;
  }

  // Dans la modale d'ajout, calculer si le montant est différent
  const montantExistant = extractAmount(existingAmount);
  const montantDiff = montantExistant && newClass.amount && Number(newClass.amount) !== Number(montantExistant);

  console.log(classes);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SecretarySidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              Gestion des Classes
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Nouvelle Classe
            </Button>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Année scolaire :</Typography>
            <FormControl size="small">
              <Select value={schoolYear} onChange={e => setSchoolYear(e.target.value)}>
                {getSchoolYears(5).map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher une classe..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {loading && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>Chargement...</Paper>
              </Grid>
            )}
            {error && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center', color: 'red' }}>{error}</Paper>
              </Grid>
            )}
            {filteredClasses.map((classe) => {
              console.log('Classe affichée:', classe);
              return (
                <Grid item xs={12} md={6} lg={4} key={classe.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                          <Typography variant="h5" component="div">
                            {classe.nom || classe.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Niveau: {classe.niveau || classe.level}
                          </Typography>
                        </Box>
                      </Box>

                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Professeur Principal"
                            secondary={classe.professeurPrincipal || classe.teacher || ''}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Effectif"
                            secondary={`${classe.students_count || 0} élèves`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Scolarité nouveaux"
                            secondary={classe.amount ? `${Number(classe.amount).toLocaleString('fr-FR')} F CFA` : 'Non définie'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Scolarité anciens"
                            secondary={classe.amount_old_students ? `${Number(classe.amount_old_students).toLocaleString('fr-FR')} F CFA` : 'Non définie'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Cantine"
                            secondary={classe.cantine_amount ? `${Number(classe.cantine_amount).toLocaleString('fr-FR')} F CFA` : 'Non définie'}
                          />
                        </ListItem>
                      </List>

                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={classe.statut || 'Active'}
                          color="success"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button 
                        size="small" 
                        variant="outlined"
                        startIcon={<EditIcon />} 
                        onClick={() => handleEditOpen(classe)}
                        sx={{ minWidth: 100 }}
                      >
                        Modifier
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        color="error" 
                        startIcon={<DeleteIcon />} 
                        onClick={() => handleDelete(classe.id)}
                        sx={{ minWidth: 100 }}
                      >
                        Supprimer
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Ajouter une nouvelle classe</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nom de la classe"
              fullWidth
              value={newClass.name}
              onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
              name="name"
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="niveau-label">Niveau</InputLabel>
              <Select
                labelId="niveau-label"
                value={newClass.level}
                label="Niveau"
                onChange={(e) => {
                  setNewClass({ ...newClass, level: e.target.value });
                  checkExistingAmount(e.target.value);
                }}
                name="level"
              >
                <MenuItem value="Maternel GS">Maternel GS</MenuItem>
                <MenuItem value="Maternel PS">Maternel PS</MenuItem>
                <MenuItem value="Maternel TPS">Maternel TPS</MenuItem>
                <MenuItem value="Maternel MS">Maternel MS</MenuItem>
              </Select>
            </FormControl>
            {existingAmount && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Montant existant pour ce niveau: {existingAmount}
              </Alert>
            )}
            {montantDiff && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Impossible d'ajouter une nouvelle classe pour ce niveau : le montant doit être identique à celui déjà existant ({montantExistant} F CFA).
              </Alert>
            )}
            <TextField
              margin="dense"
              label="Montant nouveaux élèves (F CFA)"
              type="number"
              fullWidth
              value={newClass.amount}
              onChange={(e) => setNewClass({ ...newClass, amount: e.target.value })}
              name="amount"
              placeholder="Ex: 150000"
              InputProps={{ readOnly: !!existingAmount }}
              helperText="Montant pour les nouveaux élèves"
            />
            <TextField
              margin="dense"
              label="Montant anciens élèves (F CFA)"
              type="number"
              fullWidth
              value={newClass.amount_old_students}
              onChange={(e) => setNewClass({ ...newClass, amount_old_students: e.target.value })}
              name="amount_old_students"
              placeholder="Ex: 120000"
              helperText="Montant pour les élèves déjà inscrits"
            />
            <TextField
              margin="dense"
              label="Montant de la cantine (F CFA)"
              type="number"
              fullWidth
              value={newClass.cantine_amount}
              onChange={(e) => setNewClass({ ...newClass, cantine_amount: e.target.value })}
              name="cantine_amount"
              placeholder="Ex: 75000"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button variant="contained" onClick={handleAddClass} disabled={!!montantDiff}>Ajouter</Button>
          </DialogActions>
        </Dialog>
        {/* Modale d'édition */}
        <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon color="primary" />
              Modifier la classe
            </Box>
          </DialogTitle>
          <DialogContent>
            {editClass && (
              <Box sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="Nom de la classe"
                      fullWidth
                      value={editClass.name}
                      onChange={handleEditChange}
                      name="name"
                      required
                      helperText="Nom de la classe (ex: 6ème A, 5ème B)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="edit-niveau-label">Niveau *</InputLabel>
                      <Select
                        labelId="edit-niveau-label"
                        value={editClass.level}
                        label="Niveau *"
                        onChange={(e) => {
                          handleEditChange({ target: { name: 'level', value: e.target.value } });
                          checkExistingAmount(e.target.value, editClass.id);
                        }}
                        name="level"
                        required
                      >
                        <MenuItem value="Maternel GS">Maternel GS</MenuItem>
                        <MenuItem value="Maternel PS">Maternel PS</MenuItem>
                        <MenuItem value="Maternel TPS">Maternel TPS</MenuItem>
                        <MenuItem value="Maternel MS">Maternel MS</MenuItem>
              
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {existingAmount && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <strong>Information :</strong> Montant existant pour ce niveau: {existingAmount}
                      </Alert>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Montant nouveaux élèves (F CFA) *"
                      type="number"
                      fullWidth
                      value={editClass.amount}
                      onChange={handleEditChange}
                      name="amount"
                      placeholder="Ex: 150000"
                      required
                      helperText="Montant pour les nouveaux élèves"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Montant anciens élèves (F CFA)"
                      type="number"
                      fullWidth
                      value={editClass.amount_old_students}
                      onChange={handleEditChange}
                      name="amount_old_students"
                      placeholder="Ex: 120000"
                      helperText="Montant pour les élèves déjà inscrits"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Montant de la cantine (F CFA)"
                      type="number"
                      fullWidth
                      value={editClass.cantine_amount}
                      onChange={handleEditChange}
                      name="cantine_amount"
                      placeholder="Ex: 75000"
                      helperText="Montant de la cantine (optionnel)"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <strong>Note :</strong> Toutes les classes d'un même niveau doivent avoir le même montant de scolarité pour les nouveaux élèves. Le montant pour les anciens élèves peut être différent.
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} color="inherit">
              Annuler
            </Button>
            <Button 
              variant="contained" 
              onClick={handleEditSubmit}
              disabled={!editClass?.name || !editClass?.level || !editClass?.amount}
            >
              Enregistrer les modifications
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Classes; 